import { Fragment, useState } from "react"
import { BracesIcon, ChevronDownIcon, ChevronRightIcon, TriangleAlertIcon, VariableIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { flowTitle } from "./text"

type Vars = { [key: string]: any }

export interface DebugTurn {
  trace: PlaybookTrace[]
  before: Vars
  after: Vars
}

interface Step {
  flow: string
  item: string
  skipped: boolean
  option?: string
  notes: string[]
}

const QUIET = new Set(["waiting for a message", "nothing matched, asking again"])
const CHIP = "inline-flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 font-mono hover:bg-muted"

function path(trace: PlaybookTrace[]): Step[] {
  const steps: Step[] = []
  for (const t of trace) {
    if (!t.flow || !t.item) continue
    let last = steps[steps.length - 1]
    if (!last || last.flow !== t.flow || last.item !== t.item) steps.push(last = { flow: t.flow, item: t.item, skipped: false, notes: [] })
    if (t.option) last.option = t.option
    if (t.event === "item.skipped") last.skipped = true
    if (t.note) last.notes.push(t.note)
  }
  return steps
}

function changes(before: Vars, after: Vars) {
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  return keys.filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key])).map(key => ({ key, from: before[key], to: after[key] }))
}

function change(from: any, to: any) {
  if (to === undefined) return "unset"
  return from === undefined ? `set → ${JSON.stringify(to)}` : `${JSON.stringify(from)} → ${JSON.stringify(to)}`
}

export function TurnDebug({ turn, flows, onFocus }: { turn: DebugTurn, flows: Playbook["flows"], onFocus: (cursor: PlaybookCursor) => void }) {
  const [expanded, setExpanded] = useState(false)
  const steps = path(turn.trace)
  const title = (step: Step) => flows[step.flow]?.items.find(i => i.id === step.item)?.title || step.item
  const changed = changes(turn.before, turn.after)
  const warnings = turn.trace.filter(t => t.note && !t.outcome && !QUIET.has(t.note))

  return <div className="space-y-1 text-xs text-muted-foreground">
    <div className="flex flex-wrap items-center gap-1">
      {steps.map((step, index) => <Fragment key={index}>
        {index > 0 && <ChevronRightIcon className="size-3" />}
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" onClick={() => onFocus({ flow: step.flow, item: step.item })} className={cn(CHIP, step.skipped && "line-through opacity-60")}>
              {step.flow === "main" ? title(step) : `${flowTitle(flows, step.flow)} · ${title(step)}`}
            </button>
          </TooltipTrigger>
          {!!step.notes.length && <TooltipContent>{step.notes.map((note, i) => <p key={i}>{note}</p>)}</TooltipContent>}
        </Tooltip>
        {step.option && <span>“{step.option}”</span>}
      </Fragment>)}
      <button type="button" onClick={() => setExpanded(!expanded)} className="rounded p-0.5 hover:bg-muted" aria-label="Trace">
        <ChevronDownIcon className={cn("size-3 transition-transform", expanded && "rotate-180")} />
      </button>
    </div>
    {!!changed.length && <div className="flex flex-wrap items-center gap-1">
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={CHIP}><VariableIcon className="size-3" />{changed.length} {changed.length === 1 ? "var" : "vars"}</button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto max-w-80 space-y-1 font-mono text-xs">
          {changed.map(c => <div key={c.key} className="break-all"><span className="text-muted-foreground">{c.key}</span> {change(c.from, c.to)}</div>)}
        </PopoverContent>
      </Popover>
    </div>}
    {warnings.map(w => <div key={w.seq} className="flex items-start gap-1 text-amber-600"><TriangleAlertIcon className="mt-0.5 size-3 shrink-0" />{w.note}</div>)}
    {expanded && <div className="space-y-0.5 rounded border bg-background p-2 font-mono">
      {turn.trace.map(t => <div key={t.seq} className="whitespace-pre-wrap">
        <span>{[t.flow, t.item].filter(Boolean).join(".") || "-"}</span>
        <span className="text-blue-600"> · {t.event}</span>
        {t.call && <span className="text-foreground"> · {t.call.name}</span>}
        {t.option && <span className="text-foreground"> → {t.option}</span>}
        {t.var && <span className="text-foreground"> · {t.var} = {JSON.stringify(t.value)}</span>}
        {t.outcome && <span className="text-foreground"> · {t.outcome}</span>}
        {t.note && <span className="text-amber-600"> · {t.note}</span>}
      </div>)}
    </div>}
  </div>
}

export function StateButton({ state }: { state: PlaybookState | null }) {
  const cursor = state?.cursor ? `${state.cursor.flow}.${state.cursor.item}` : "closed"
  const vars = Object.entries(state?.vars ?? {})
  const notes = Object.entries(state?.notes ?? {}).flatMap(([scope, entries]) => Object.entries(entries).map(([key, text]) => [`${scope} · ${key}`, text]))

  return <Popover>
    <Tooltip>
      <TooltipTrigger asChild>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" aria-label="State"><BracesIcon /></Button>
        </PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent><p>State</p></TooltipContent>
    </Tooltip>
    <PopoverContent align="end" className="max-h-96 w-80 space-y-3 overflow-auto font-mono text-xs">
      <section className="space-y-1">
        <div className="font-sans font-medium text-muted-foreground">Cursor</div>
        <div>{cursor}</div>
      </section>
      <section className="space-y-1">
        <div className="font-sans font-medium text-muted-foreground">Vars</div>
        {vars.length ? vars.map(([key, value]) => <div key={key} className="break-all"><span className="text-muted-foreground">{key}</span> = {JSON.stringify(value)}</div>) : <div className="text-muted-foreground">none</div>}
      </section>
      {!!notes.length && <section className="space-y-1">
        <div className="font-sans font-medium text-muted-foreground">Notes</div>
        {notes.map(([key, text]) => <div key={key} className="break-all"><span className="text-muted-foreground">{key}</span>: {text}</div>)}
      </section>}
    </PopoverContent>
  </Popover>
}
